// 관리자 대시보드 — 베타 기간 사용 현황을 **읽기 전용**으로 본다.
//
// 설계 원칙 (여기서 어기면 안 되는 것):
//   · 대화를 수정·삭제하는 조작은 만들지 않는다. 서버에도 GET 외 엔드포인트가 없다.
//   · 이 화면의 권한 판단은 편의일 뿐이다 — 서버가 매 요청 다시 본다.
//   · 남의 대화를 여는 행위이므로 조회는 서버에 감사 로그로 남는다.
//
// 질문 유형 분류는 넣지 않았다. 원문을 그대로 쌓고 검색·정렬로 훑는다 —
// 베타 초기에 분류 기준을 정해버리면 예상 못 한 사용 패턴을 놓친다.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addAdmin,
  compact,
  fetchConversation,
  fetchLogs,
  fetchOverview,
  fetchRoster,
  fetchSettings,
  fetchUsage,
  modelCostUsd,
  type LogSort,
  ms,
  reloadMcp,
  removeAdmin,
  updateSetting,
  usd,
  when,
  type LogItem,
  type Overview,
  type RosterItem,
  type RosterRole,
  type SettingItem,
  type UsageRow,
  fetchAbuse,
  setBlocked,
  forceLogout,
  type AbuseRow,
} from "../lib/adminApi";
import ContactFooter from "../components/ContactFooter";
import { useAuth } from "../stores/auth";

type Tab = "overview" | "chats" | "users" | "abuse" | "roster";
const TABS: { key: Tab; label: string; managerOnly?: boolean }[] = [
  { key: "overview", label: "개요" },
  { key: "chats", label: "대화" },
  { key: "users", label: "사용자" },
  // 남용 — 스크래퍼 의심 계정과 차단. 조작은 최고관리자만
  { key: "abuse", label: "남용", managerOnly: true },
  // 명단 관리 — 최고관리자에게만 보인다 (숨김은 편의, 권한은 서버가 다시 본다)
  { key: "roster", label: "명단", managerOnly: true },
];
const RANGES = [7, 30, 90];
const LAB_PAGE = 100;  // 조회 내역 한 페이지

const FEATURE_KO: Record<string, string> = {
  buffett: "버핏스코어",
  peer_ai: "유사기업 (사업내용 AI)",
  industry: "산업분류 스크리닝",
  precedent: "선례 검색",
  dcf: "DCF 워크스페이스",
  market_answer: "시장 이슈 — 질문 상세",
  market_docs: "시장 이슈 — 문서 탭",
  market_company: "시장 이슈 — 기업 조회",
  re: "부동산 매매사례 조회",
  re_report: "부동산 검토보고서 (PDF·엑셀)",
};

function featureKo(f: string): string {
  return FEATURE_KO[f] || f;
}

const DEVICE_KO: Record<string, string> = { pc: "PC", mobile: "모바일", tablet: "태블릿" };

// 한도 입력칸 — [설정키 접미사, 라벨, 비웠을 때 실제 적용값].
// 대부분은 비우면 무제한이지만 시장 이슈 둘만 서버 기본값이 있다(수집 표적이라
// 관리자가 손대기 전부터 켜둔다). 자리표시자에 그 값을 그대로 보여주지 않으면
// "무제한"으로 읽혀 실제 동작과 어긋난다 — quota.py 의 기본값과 같아야 한다.
const DAILY_FIELDS = [
  ["chat", "채팅(턴)", "무제한"],
  // 베타(구글 로그인) 전용 겹 — 일반 chat 한도와 별개. 빈값·0 = 무제한,
  // 숫자 = 그 한도 (다른 항목들과 같은 규칙 — 사용자 확정)
  ["beta_turns", "베타 채팅(턴)", "무제한"],
  ["market_docs", "시장 이슈 문서", "300"],
  ["market_answer", "시장 이슈 답변", "50"],
  ["buffett", "버핏스코어", "무제한"],
  ["peer", "유사기업", "무제한"],
  ["dcf", "DCF 업로드", "무제한"],
  ["report", "기업조사 리포트", "무제한"],
  ["re", "부동산 매매사례 조회", "무제한"],
] as const;

const BURST_FIELDS = [
  ["market_docs", "시장 이슈 문서", "20"],
  ["market_answer", "시장 이슈 답변", "10"],
] as const;

/** 접속 기기 표시. 구글 베타 로그인 이전 사용자는 기록이 없어 '–' 로 둔다. */
function deviceLabel(devices?: string | null): string {
  const parts = (devices || "").split(",").map((x) => x.trim()).filter(Boolean);
  if (!parts.length) return "–";
  return parts.map((x) => DEVICE_KO[x] || x).join(" · ");
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="adm-stat">
      <span className="adm-stat-l">{label}</span>
      <strong className="adm-stat-v">{value}</strong>
      {sub ? <span className="adm-stat-s">{sub}</span> : null}
    </div>
  );
}

/** 일별 추이 — 라이브러리 없이 막대로. 값의 크기 비교만 되면 충분하다. */
function Spark({ daily }: { daily: Overview["daily"] }) {
  if (!daily.length) return <p className="adm-empty">아직 기록이 없습니다.</p>;
  const max = Math.max(...daily.map((d) => d.turns), 1);
  return (
    <div className="adm-spark" role="img" aria-label="일별 턴 수 추이">
      {daily.map((d) => (
        <div className="adm-spark-col" key={d.day} title={`${d.day} · ${d.turns}턴 · ${d.users}명`}>
          <div className="adm-spark-bar" style={{ height: `${Math.max(4, (d.turns / max) * 100)}%` }} />
          <span className="adm-spark-x">{d.day.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminPage() {
  const { user, checking } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");
  const [days, setDays] = useState(30);
  const [ov, setOv] = useState<Overview | null>(null);
  const [logs, setLogs] = useState<LogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [modelFilter, setModelFilter] = useState("");
  const [toolFilter, setToolFilter] = useState("");
  const [toolOk, setToolOk] = useState<"" | "0" | "1">("");
  const [sort, setSort] = useState<LogSort>("recent");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState<{ id: string; items: LogItem[] } | null>(null);
  // 조회 내역 필터 — 서버 재조회 없이 이미 받은 lab_recent 를 좁힌다(최대 300건)
  const [labFeature, setLabFeature] = useState("");
  const [labQuery, setLabQuery] = useState("");
  const labAll = ov?.lab_recent || [];
  const labFeatures = Array.from(new Set(labAll.map((r) => r.feature))).sort();
  const labNeedle = labQuery.trim().toLowerCase();
  const labRows = labAll.filter(
    (r) => (!labFeature || r.feature === labFeature)
      && (!labNeedle || r.detail.toLowerCase().includes(labNeedle)
          || r.user_uid.toLowerCase().includes(labNeedle)),
  );
  // 페이지 — 한 번에 100건만 그린다. 300건을 다 펼치면 스크롤이 표에 잡아먹힌다.
  const [labPage, setLabPage] = useState(1);
  const labPages = Math.max(1, Math.ceil(labRows.length / LAB_PAGE));
  const labPageRows = labRows.slice((labPage - 1) * LAB_PAGE, labPage * LAB_PAGE);
  const [roster, setRoster] = useState<RosterItem[]>([]);
  const [abuse, setAbuse] = useState<AbuseRow[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<RosterRole>("admin");
  const [blockEmail, setBlockEmail] = useState("");
  const [settings, setSettings] = useState<Record<string, SettingItem>>({});
  const [settingBusy, setSettingBusy] = useState(false);
  const [betaClientId, setBetaClientId] = useState("");
  const [gptModel, setGptModel] = useState("");
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [burst, setBurst] = useState<Record<string, string>>({});
  const [usage, setUsage] = useState<UsageRow[]>([]);
  const [usageDay, setUsageDay] = useState("");
  const [mcpBusy, setMcpBusy] = useState(false);
  const [mcpMsg, setMcpMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    document.title = "관리자 대시보드 · Valuation Agent";
    return () => {
      document.title = "Valuation Agent · DeepSearch";
    };
  }, []);

  const isAdmin = !!user?.is_admin;

  const loadOverview = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      setOv(await fetchOverview(days));
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  }, [days]);

  const loadLogs = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const p = await fetchLogs({ days, q, user: userFilter, errors_only: errorsOnly,
        model: modelFilter, tool: toolFilter, tool_ok: toolOk, sort, limit: 50 });
      setLogs(p.items);
      setTotal(p.total);
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  }, [days, q, userFilter, errorsOnly, modelFilter, toolFilter, toolOk, sort]);

  const loadAbuse = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      {
        const d = await fetchAbuse(24);
        // 차단해 둔 계정이 조용해지면(신호 없음) 목록에서 사라져 해제할 길이
        // 없었다 — API 가 따로 주는 차단 전체 목록을 최소 행으로 합쳐 둔다.
        const seen = new Set(d.items.map((x) => x.uid));
        const quiet = (d.blocked || []).filter((u) => !seen.has(u)).map((u) => ({
          uid: u, requests: 0, distinctKeywords: 0, deepPages: 0,
          signalLabels: [], suspect: false, lastSeen: null, blocked: true,
        } satisfies AbuseRow));
        setAbuse([...d.items, ...quiet]);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  const loadRoster = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      setRoster(await fetchRoster());
      const st = await fetchSettings();
      setSettings(st);
      setBetaClientId(st.beta_google_client_id?.value || "");
      setGptModel(st.gpt_model?.value || "");
      setLimits(Object.fromEntries(
        DAILY_FIELDS.map(([k]) => [k, st[`limit_daily_${k}`]?.value || ""])));
      setBurst(Object.fromEntries(
        BURST_FIELDS.map(([k]) => [k, st[`limit_burst_${k}`]?.value || ""])));
      try {
        const rows = await fetchUsage(7);
        setUsage(rows);
        if (rows.length && !usageDay) setUsageDay(rows.map((r) => r.day).sort().pop() || "");
      } catch { /* 사용량 실패해도 설정 화면은 뜬다 */ }
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "chats") void loadLogs();
    else if (tab === "abuse") void loadAbuse();
    else if (tab === "roster") void loadRoster();
    else void loadOverview();
  }, [isAdmin, tab, loadLogs, loadOverview, loadRoster]);

  // 대화 탭의 모델·도구 필터 옵션은 개요 데이터에서 나온다 — 없으면 한 번 로드
  useEffect(() => {
    if (isAdmin && tab === "chats" && !ov) void loadOverview();
  }, [isAdmin, tab, ov, loadOverview]);

  if (checking) return <div className="v2-boot">확인 중…</div>;

  if (!isAdmin) {
    return (
      <div className="admin-root">
        <div className="admin-deny">
          <h1>접근 권한이 없습니다</h1>
          <p>
            관리자로 지정된 계정만 열 수 있습니다.
            {user?.username ? ` 현재 ${user.username} 으로 로그인되어 있습니다.` : ""}
          </p>
          <button className="rp-excel" type="button" onClick={() => navigate("/")}>
            채팅으로 돌아가기
          </button>
        </div>
      </div>
    );
  }

  const t = ov?.totals;

  return (
    <div className="admin-root">
      <header className="admin-head">
        <div>
          <span className="admin-eyebrow">관리자</span>
          <h1>베타 사용 현황</h1>
        </div>
        <select
          className="adm-select"
          value={days}
          aria-label="조회 기간"
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {RANGES.map((d) => (
            <option value={d} key={d}>
              최근 {d}일
            </option>
          ))}
        </select>
        <button className="rp-excel" type="button" onClick={() => navigate("/")}>
          채팅으로
        </button>
      </header>

      <p className="admin-note">
        대화는 <strong>읽기 전용</strong>입니다. 이 화면에서 사용자의 대화를 수정하거나 지울 수 없고,
        조회 기록은 서버에 남습니다.
      </p>

      <nav className="adm-tabs">
        {TABS.filter((x) => !x.managerOnly || user?.is_manager).map((x) => (
          <button
            key={x.key}
            type="button"
            className={"adm-tab" + (tab === x.key ? " on" : "")}
            aria-current={tab === x.key}
            onClick={() => setTab(x.key)}
          >
            {x.label}
          </button>
        ))}
        {busy ? <span className="adm-busy">불러오는 중…</span> : null}
      </nav>

      {err ? <p className="adm-err">{err}</p> : null}

      {tab === "overview" && ov ? (
        <>
          <div className="adm-stats">
            <Stat label="질문 수" value={compact(t!.turns)} sub={`대화 ${compact(t!.conversations)}개`} />
            <Stat label="사용자" value={compact(t!.users)} sub={`삭제된 대화 ${t!.deleted_conversations}`} />
            <Stat label="평균 응답" value={ms(t!.avg_ms)} />
            <Stat
              label="실패"
              value={compact(t!.errors)}
              sub={t!.turns ? `${((t!.errors / t!.turns) * 100).toFixed(1)}%` : "0%"}
            />
            <Stat
              label="토큰"
              value={compact(t!.tokens.input + t!.tokens.output)}
              sub={`입력 ${compact(t!.tokens.input)} · 출력 ${compact(t!.tokens.output)}`}
            />
            <Stat
              label="캐시 적중"
              value={compact(t!.tokens.cache_read)}
              sub={`생성 ${compact(t!.tokens.cache_write)}`}
            />
          </div>

          <section className="adm-sec">
            <h2>일별 질문 수</h2>
            <Spark daily={ov.daily} />
          </section>

          <section className="adm-sec">
            <h2>Graph DB 연결</h2>
            <p className="adm-empty" style={{ marginBottom: 12 }}>
              Graph DB(MCP) 서버가 도구를 개편하면 실행 중인 백엔드는 재배포 전까지 옛 도구
              목록을 캐시한 채 물고 있습니다. 이 버튼은 세션·도구 캐시를 버리고 다시 붙습니다 —
              사용자 대화에는 영향이 없습니다.
            </p>
            <div className="adm-filters">
              <button
                className="rp-excel"
                type="button"
                disabled={mcpBusy}
                onClick={() =>
                  void (async () => {
                    setMcpBusy(true);
                    setMcpMsg(null);
                    try {
                      const r = await reloadMcp();
                      // 서버 개편 감지 — 신규/폐지 도구가 보이면 동기화 작업 신호를 그대로 띄운다
                      const drift: string[] = [];
                      if (r.sync?.new?.length) drift.push(`신규 ${r.sync.new.length}종: ${r.sync.new.join(", ")}`);
                      if (r.sync?.removed?.length) drift.push(`폐지 ${r.sync.removed.length}종: ${r.sync.removed.join(", ")}`);
                      const tail = drift.length ? ` · ⚠ 서버 개편 감지 — ${drift.join(" / ")} (동기화 필요)` : "";
                      setMcpMsg({ ok: true, text: `재연결 완료 — 도구 ${r.count}개 (${when(new Date().toISOString())})${tail}` });
                    } catch (er) {
                      setMcpMsg({ ok: false, text: String((er as Error).message || er) });
                    } finally {
                      setMcpBusy(false);
                    }
                  })()
                }
              >
                {mcpBusy ? "재연결 중…" : "MCP 재연결"}
              </button>
              {mcpMsg ? (
                <span className={"adm-badge" + (mcpMsg.ok ? "" : " bad")}>{mcpMsg.text}</span>
              ) : null}
            </div>
          </section>

          <section className="adm-sec">
            <h2>모델별 사용량 · 예상 비용</h2>
            {(ov.models || []).length ? (
              <>
                <div className="adm-tablewrap">
                  <table className="adm-table">
                    <thead>
                      <tr>
                        <th>모델</th>
                        <th className="n">턴</th>
                        <th className="n">입력 토큰</th>
                        <th className="n">출력 토큰</th>
                        <th className="n">캐시 읽기</th>
                        <th className="n">캐시 쓰기</th>
                        <th className="n">예상 비용</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(ov.models || []).map((m) => {
                        const cost = modelCostUsd(m);
                        return (
                          <tr key={m.model}>
                            <td className="mono">{m.model}</td>
                            <td className="n">{compact(m.turns)}</td>
                            <td className="n">{compact(m.in_tok)}</td>
                            <td className="n">{compact(m.out_tok)}</td>
                            <td className="n">{compact(m.cache_read)}</td>
                            <td className="n">{compact(m.cache_write)}</td>
                            {/* (미호출·…) 행은 모델 호출이 없던 턴 — 단가 개념이 없다 */}
                            <td className="n">{m.model.startsWith("(") ? "—" : cost == null ? "단가 미등록" : usd(cost)}</td>
                          </tr>
                        );
                      })}
                      <tr>
                        <td colSpan={6} style={{ textAlign: "right", fontWeight: 600 }}>합계</td>
                        <td className="n" style={{ fontWeight: 600 }}>
                          {usd((ov.models || []).reduce((a, m) => a + (modelCostUsd(m) ?? 0), 0))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="adm-empty" style={{ fontSize: 12, marginTop: 6 }}>
                  단가표(코드의 MODEL_PRICES, USD/1M tokens) 기준 추정치입니다 — 실제 청구액과
                  다를 수 있습니다. 캐시 쓰기 단가가 0인 모델은 해당 항목을 비용에 넣지 않습니다.
                </p>
              </>
            ) : (
              <p className="adm-empty">아직 모델 사용 기록이 없습니다.</p>
            )}
          </section>

          <section className="adm-sec">
            <h2>기능 사용 — 버핏스코어 · 유사기업 · DCF · 부동산</h2>
            {ov.labs?.length ? (
              <div className="adm-tablewrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>기능</th><th className="n">사용</th><th className="n">사용자</th><th>최근 사용</th></tr>
                  </thead>
                  <tbody>
                    {ov.labs.map((x) => (
                      <tr key={x.feature}>
                        <td>{featureKo(x.feature)}</td>
                        <td className="n">{x.uses.toLocaleString()}</td>
                        <td className="n">{x.users.toLocaleString()}</td>
                        <td>{(x.last_at || "").slice(0, 16).replace("T", " ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="adm-empty">아직 기능 사용 기록이 없습니다 (버핏스코어·유사기업·DCF·부동산 매매사례 실행 시 수집).</p>
            )}
          </section>

          {/* 건수만으로는 어떤 회사·사업내용·이슈를 봤는지 알 수 없다 — 조회 내용 원문을 그대로 */}
          <section className="adm-sec">
            <h2>조회 내역 — 무엇을 조회했는지</h2>
            <div className="adm-filters">
              <select value={labFeature}
                onChange={(e) => { setLabFeature(e.target.value); setLabPage(1); }}>
                <option value="">전체 기능</option>
                {labFeatures.map((f) => <option key={f} value={f}>{featureKo(f)}</option>)}
              </select>
              <input value={labQuery} placeholder="조회 내용·계정 검색"
                onChange={(e) => { setLabQuery(e.target.value); setLabPage(1); }} />
            </div>
            {labRows.length ? (
              <div className="adm-tablewrap">
                <table className="adm-table">
                  <thead>
                    <tr><th>시각</th><th>계정</th><th>기능</th><th>조회 내용</th></tr>
                  </thead>
                  <tbody>
                    {labPageRows.map((r, i) => (
                      <tr key={`${r.at}-${i}`}>
                        <td>{when(r.at)}</td>
                        <td className="mono">{r.user_uid}</td>
                        <td>{featureKo(r.feature)}</td>
                        <td className="adm-detail">{r.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {labPages > 1 && (
                  <div className="adm-pager">
                    {Array.from({ length: labPages }, (_, i) => i + 1).map((n) => (
                      <button key={n} type="button"
                        className={`adm-page${n === labPage ? " on" : ""}`}
                        onClick={() => setLabPage(n)}>{n}</button>
                    ))}
                    <span className="adm-page-note">
                      {labRows.length.toLocaleString("ko-KR")}건 중 {(labPage - 1) * LAB_PAGE + 1}–
                      {Math.min(labPage * LAB_PAGE, labRows.length)}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="adm-empty">
                {ov.lab_recent?.length ? "조건에 맞는 조회가 없습니다." :
                  "아직 조회 기록이 없습니다 (버핏스코어·유사기업·산업분류·선례·시장 이슈·부동산 매매사례 조회 시 수집)."}
              </p>
            )}
          </section>

          <section className="adm-sec">
            <h2>많이 쓰인 도구</h2>
            {ov.tools.length ? (
              <div className="adm-tablewrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>도구</th>
                      <th className="n">호출</th>
                      <th className="n">실패</th>
                      <th className="n">평균 소요</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ov.tools.map((x) => (
                      <tr key={x.name}>
                        <td className="mono">{x.name}</td>
                        <td className="n">
                          <button className="adm-link" type="button"
                            title={`${x.name} 를 호출한 대화 보기`}
                            onClick={() => { setToolFilter(x.name); setToolOk(""); setTab("chats"); }}>
                            {compact(x.calls)}
                          </button>
                        </td>
                        <td className="n">
                          {x.fails ? (
                            <button className="adm-link adm-bad" type="button"
                              title={`${x.name} 실패가 발생한 대화 보기`}
                              onClick={() => { setToolFilter(x.name); setToolOk("0"); setTab("chats"); }}>
                              {x.fails}
                            </button>
                          ) : "–"}
                        </td>
                        <td className="n">{ms(x.avg_ms)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="adm-empty">아직 도구 호출 기록이 없습니다.</p>
            )}
          </section>
        </>
      ) : null}

      {tab === "users" && ov ? (
        <section className="adm-sec">
          <h2>사용자별 사용량</h2>
          {ov.users.length ? (
            <div className="adm-tablewrap">
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>계정</th>
                    <th className="n">질문</th>
                    <th className="n">대화</th>
                    <th className="n">실패</th>
                    <th className="n">평균 응답</th>
                    <th className="n">토큰</th>
                    <th>기기</th>
                    <th>최초 접속</th>
                    <th>마지막 사용</th>
                  </tr>
                </thead>
                <tbody>
                  {ov.users.map((u) => (
                    <tr key={u.user_uid}>
                      <td className="mono">
                        <button
                          className="adm-link"
                          type="button"
                          onClick={() => {
                            setUserFilter(u.user_uid);
                            setTab("chats");
                          }}
                        >
                          {u.user_uid}
                        </button>
                      </td>
                      <td className="n">{compact(u.turns)}</td>
                      <td className="n">{compact(u.conversations)}</td>
                      <td className="n">{u.errors ? <span className="adm-bad">{u.errors}</span> : "–"}</td>
                      <td className="n">{ms(u.avg_ms)}</td>
                      <td className="n">{compact(u.in_tok + u.out_tok)}</td>
                      <td>{deviceLabel(u.devices)}</td>
                      <td>{when(u.first_at || null)}</td>
                      <td>{when(u.last_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="adm-empty">아직 사용 기록이 없습니다.</p>
          )}
        </section>
      ) : null}

      {tab === "chats" ? (
        <section className="adm-sec">
          <div className="adm-filters">
            <input
              className="adm-input"
              placeholder="질문·답변 검색"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void loadLogs();
              }}
            />
            <input
              className="adm-input"
              placeholder="계정으로 좁히기"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void loadLogs();
              }}
            />
            <select className="adm-select" value={modelFilter} aria-label="모델 필터"
                    onChange={(e) => setModelFilter(e.target.value)}>
              <option value="">모델 전체</option>
              {(ov?.models || []).map((m) => (
                <option key={m.model} value={m.model}>{m.model}</option>
              ))}
            </select>
            <select className="adm-select" value={toolFilter} aria-label="도구 필터"
                    onChange={(e) => setToolFilter(e.target.value)}>
              <option value="">도구 전체</option>
              {(ov?.tools || []).map((x) => (
                <option key={x.name} value={x.name}>{x.name}</option>
              ))}
            </select>
            {toolFilter ? (
              <select className="adm-select" value={toolOk} aria-label="도구 결과"
                      onChange={(e) => setToolOk(e.target.value as "" | "0" | "1")}>
                <option value="">성공+실패</option>
                <option value="1">성공만</option>
                <option value="0">실패만</option>
              </select>
            ) : null}
            <select className="adm-select" value={sort} aria-label="정렬"
                    onChange={(e) => setSort(e.target.value as LogSort)}>
              <option value="recent">최신순</option>
              <option value="tokens">토큰 사용량순</option>
              <option value="duration">소요시간순</option>
            </select>
            <label className="adm-check">
              <input
                type="checkbox"
                checked={errorsOnly}
                onChange={(e) => setErrorsOnly(e.target.checked)}
              />
              턴 실패만
            </label>
            <button className="rp-excel" type="button" onClick={() => void loadLogs()}>
              검색
            </button>
            <span className="adm-count">{compact(total)}건</span>
          </div>

          {logs.length ? (
            <ul className="adm-logs">
              {logs.map((l) => (
                <li className="adm-log" key={l.id}>
                  <div className="adm-log-head">
                    <span className="mono adm-who">{l.user_uid}</span>
                    <span className="adm-when">{when(l.created_at)}</span>
                    {l.error ? <span className="adm-badge bad">실패</span> : null}
                    {l.model ? <span className="adm-badge">{l.model.replace("claude-", "")}</span> : null}
                    <span className="adm-badge">{ms(l.duration_ms)}</span>
                    <span className="adm-badge">{compact(l.tokens.input + l.tokens.output)} 토큰</span>
                    {l.conversation_id ? (
                      <button
                        className="adm-link"
                        type="button"
                        onClick={async () => {
                          try {
                            setDetail({ id: l.conversation_id!, items: await fetchConversation(l.conversation_id!) });
                          } catch (e) {
                            setErr(String((e as Error).message || e));
                          }
                        }}
                      >
                        대화 전체
                      </button>
                    ) : null}
                  </div>
                  <p className="adm-q">{l.question || "(질문 없음)"}</p>
                  <p className="adm-a">{l.answer ? l.answer.slice(0, 400) : "(답변 없음)"}</p>
                  {l.tools.length || l.files.length ? (
                    <div className="adm-chips">
                      {l.tools.map((x, i) => (
                        <span className={"adm-chip" + (x.ok ? "" : " bad")} key={i}>
                          {x.name}
                        </span>
                      ))}
                      {l.files.map((f, i) => (
                        <a className="adm-chip file" key={"f" + i} href={f.url} target="_blank" rel="noopener">
                          {f.name}
                        </a>
                      ))}
                    </div>
                  ) : null}
                  {l.error ? <p className="adm-errline">{l.error}</p> : null}
                </li>
              ))}
            </ul>
          ) : (
            <p className="adm-empty">조건에 맞는 기록이 없습니다.</p>
          )}
        </section>
      ) : null}

      {tab === "abuse" ? (
        <section className="adm-sec">
          <h2>스크래퍼 의심 계정</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            신호가 2개 이상이면 의심으로 봅니다. 자동 차단은 하지 않습니다 —
            오탐으로 거래처를 막는 편이 더 큰 사고라 판단은 사람이 합니다.
            최근 24시간의 시장 이슈 문서 조회를 봅니다.
          </p>
          {user?.is_manager ? (
            <div className="adm-filters" style={{ marginBottom: 12 }}>
              <input className="adm-input" style={{ width: 280 }}
                placeholder="직접 차단할 계정 (예: name@gmail.com)"
                value={blockEmail}
                onChange={(e) => setBlockEmail(e.target.value)} />
              <button type="button" className="adm-link" disabled={!blockEmail.trim()}
                onClick={() => void setBlocked(blockEmail.trim(), true)
                  .then(() => { setBlockEmail(""); return loadAbuse(); })
                  .catch((e) => setErr(e instanceof Error ? e.message : String(e)))}>
                차단
              </button>
            </div>
          ) : null}
          {!abuse.length && !busy ? (
            <p className="adm-empty">의심 신호가 있는 계정이 없습니다.</p>
          ) : (
            <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>계정</th>
                  <th>최근 활동</th>
                  <th>24시간 조회</th>
                  <th>검색어</th>
                  <th>깊은 페이지</th>
                  <th>신호</th>
                  <th>조치</th>
                </tr>
              </thead>
              <tbody>
                {abuse.map((a) => (
                  <tr key={a.uid}>
                    <td className="mono">{a.uid}</td>
                    <td>{a.lastSeen ? when(new Date(a.lastSeen * 1000).toISOString()) : "–"}</td>
                    <td className="n">{a.requests.toLocaleString("ko-KR")}</td>
                    <td className="n">{a.distinctKeywords}</td>
                    <td className="n">{a.deepPages}</td>
                    <td>{a.suspect ? <span className="adm-bad">{a.signalLabels.join(" · ")}</span>
                      : a.signalLabels.length ? a.signalLabels.join(" · ")
                      : a.blocked ? "차단됨 (최근 신호 없음)" : ""}</td>
                    <td>
                      {/* 로그아웃은 다시 로그인하면 풀린다(가벼운 조치),
                          차단은 관리자가 풀어야 한다 — 라벨로 구분해 둔다 */}
                      <button type="button" className="adm-link"
                              onClick={() => void forceLogout(a.uid).then(loadAbuse).catch(
                                (e) => setErr(e instanceof Error ? e.message : String(e)))}>
                        로그아웃
                      </button>
                      <button type="button" className="adm-link"
                              style={{ marginLeft: 10 }}
                              onClick={() => void setBlocked(a.uid, !a.blocked).then(loadAbuse).catch(
                                (e) => setErr(e instanceof Error ? e.message : String(e)))}>
                        {a.blocked ? "차단 해제" : "차단"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </section>
      ) : null}

      {tab === "roster" ? (
        <section className="adm-sec">
          <h2>접속 명단</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            명단의 계정은 Enterprise 구독이 없어도 로그인할 수 있습니다. 관리자는 대시보드도 열람하고,
            사용자는 로그인만 합니다. 추가/제거는 최고관리자만 할 수 있고, 기본 관리자는 제거할 수
            없습니다. 대화 데이터에는 영향이 없습니다.
          </p>
          {user?.is_manager ? (
            <div className="adm-filters">
              <input
                className="adm-input"
                placeholder="추가할 이메일 (예: name@deepsearch.com)"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newEmail.trim()) {
                    void (async () => {
                      try {
                        await addAdmin(newEmail.trim(), newRole);
                        setNewEmail("");
                        await loadRoster();
                      } catch (er) {
                        setErr(String((er as Error).message || er));
                      }
                    })();
                  }
                }}
              />
              <select
                className="adm-input"
                aria-label="구분"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as RosterRole)}
              >
                <option value="admin">관리자</option>
                <option value="user">사용자</option>
              </select>
              <button
                className="rp-excel"
                type="button"
                disabled={!newEmail.trim()}
                onClick={() =>
                  void (async () => {
                    try {
                      await addAdmin(newEmail.trim(), newRole);
                      setNewEmail("");
                      await loadRoster();
                    } catch (er) {
                      setErr(String((er as Error).message || er));
                    }
                  })()
                }
              >
                추가
              </button>
            </div>
          ) : null}
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>계정</th>
                  <th>구분</th>
                  <th>추가한 사람</th>
                  <th>추가일</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {roster.map((r) => (
                  <tr key={r.email}>
                    <td className="mono">{r.email}</td>
                    <td>
                      {r.manager ? (
                        <span className="adm-badge">최고관리자</span>
                      ) : r.builtin ? (
                        <span className="adm-badge">기본 관리자</span>
                      ) : r.role === "user" ? (
                        <span className="adm-badge">사용자</span>
                      ) : (
                        <span className="adm-badge">관리자</span>
                      )}
                    </td>
                    <td className="mono">{r.added_by || "–"}</td>
                    <td>{r.created_at ? when(r.created_at) : "–"}</td>
                    <td>
                      {user?.is_manager && !r.builtin ? (
                        <button
                          className="adm-link"
                          type="button"
                          onClick={() =>
                            void (async () => {
                              const role = r.role ?? "admin";
                              const ask =
                                role === "user"
                                  ? `${r.email} 을 사용자 명단에서 뺄까요? 다음 확인부터 로그인이 막힙니다(Enterprise 계정 제외).`
                                  : `${r.email} 의 관리자 권한을 제거할까요?`;
                              if (!window.confirm(ask)) return;
                              try {
                                await removeAdmin(r.email, role);
                                await loadRoster();
                              } catch (er) {
                                setErr(String((er as Error).message || er));
                              }
                            })()
                          }
                        >
                          제거
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 style={{ marginTop: 28 }}>접근 정책</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            DeepSearch 아이디·비밀번호 로그인은 개인 또는 소속 조직의 활성 Enterprise 구독이 있는
            계정과 위 명단(관리자·사용자)에 있는 계정만 이용할 수 있습니다. 명단에 없는
            Basic·Pro·Premium 계정은 로그인할 수 없습니다.
          </p>

          <h2 style={{ marginTop: 28 }}>마케팅 베타 (구글 로그인)</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            켜면 로그인 화면에 "Google로 계속하기" 버튼이 나타나고, 구글 계정으로 들어온
            베타 사용자의 대화·사용량이 정식 계정과 같은 파이프라인으로 수집됩니다
            (하루 턴 수 제한 적용). 끄면 즉시 — 버튼이 사라지고 기존 베타 세션도 전부
            무효화됩니다. 베타가 끝나면 여기서 끄기만 하면 됩니다.
          </p>
          <div className="adm-filters" style={{ alignItems: "center", flexWrap: "wrap" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: user?.is_manager ? "pointer" : "default" }}>
              <input
                type="checkbox"
                checked={settings.beta_google_login?.value === "1"}
                disabled={!user?.is_manager || settingBusy}
                onChange={(e) =>
                  void (async () => {
                    const next = e.target.checked ? "1" : "0";
                    if (next === "1" && !(settings.beta_google_client_id?.value || "").trim()) {
                      setErr("먼저 구글 OAuth 클라이언트 ID를 입력·저장하세요.");
                      await loadRoster();
                      return;
                    }
                    setSettingBusy(true);
                    try {
                      await updateSetting("beta_google_login", next);
                      setSettings(await fetchSettings());
                    } catch (er) {
                      setErr(String((er as Error).message || er));
                    } finally {
                      setSettingBusy(false);
                    }
                  })()
                }
              />
              <span>베타 구글 로그인 허용</span>
            </label>
            {user?.is_manager ? (
              <>
                <input
                  className="adm-input"
                  style={{ minWidth: 320 }}
                  placeholder="구글 OAuth 클라이언트 ID (…apps.googleusercontent.com)"
                  value={betaClientId}
                  onChange={(e) => setBetaClientId(e.target.value)}
                />
                <button
                  className="rp-excel"
                  type="button"
                  disabled={settingBusy}
                  onClick={() =>
                    void (async () => {
                      setSettingBusy(true);
                      try {
                        await updateSetting("beta_google_client_id", betaClientId.trim());
                        setSettings(await fetchSettings());
                      } catch (er) {
                        setErr(String((er as Error).message || er));
                      } finally {
                        setSettingBusy(false);
                      }
                    })()
                  }
                >
                  ID 저장
                </button>
              </>
            ) : null}
          </div>

          <h2 style={{ marginTop: 28 }}>모델 (LLM 공급자)</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            채팅·DCF 에이전트가 쓸 모델을 고릅니다. GPT 를 선택하면 라우팅 없이 지정 모델을
            전 구간에 사용합니다. API 키는 배포 시크릿(GPT_API_KEY)으로 주입되며, 키가 없는
            환경은 Claude 로 자동 폴백합니다. 변경은 다음 턴부터(최대 1분 캐시) 반영됩니다.
          </p>
          <div className="adm-filters" style={{ alignItems: "center", flexWrap: "wrap" }}>
            {(["claude", "gpt"] as const).map((prov) => (
              <label key={prov} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: user?.is_manager ? "pointer" : "default" }}>
                <input
                  type="radio"
                  name="llm-provider"
                  checked={(settings.llm_provider?.value || "gpt") === prov}
                  disabled={!user?.is_manager || settingBusy}
                  onChange={() =>
                    void (async () => {
                      setSettingBusy(true);
                      try {
                        await updateSetting("llm_provider", prov);
                        setSettings(await fetchSettings());
                      } catch (er) {
                        setErr(String((er as Error).message || er));
                      } finally {
                        setSettingBusy(false);
                      }
                    })()
                  }
                />
                <span>{prov === "claude" ? "Claude (sonnet/haiku 라우팅)" : "GPT (기본 — gpt-6-luna)"}</span>
              </label>
            ))}
          </div>
          {user?.is_manager ? (
            <div className="adm-filters" style={{ alignItems: "center", flexWrap: "wrap", marginTop: 8 }}>
              <input
                className="adm-input"
                style={{ minWidth: 200 }}
                placeholder="GPT 모델 ID (기본 gpt-6-luna)"
                value={gptModel}
                onChange={(e) => setGptModel(e.target.value)}
              />
              <button
                className="rp-excel"
                type="button"
                disabled={settingBusy}
                onClick={() =>
                  void (async () => {
                    setSettingBusy(true);
                    try {
                      await updateSetting("gpt_model", gptModel.trim());
                      setSettings(await fetchSettings());
                    } catch (er) {
                      setErr(String((er as Error).message || er));
                    } finally {
                      setSettingBusy(false);
                    }
                  })()
                }
              >
                저장
              </button>
            </div>
          ) : null}

          <h2 style={{ marginTop: 28 }}>운영 알림 (슬랙)</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            IR 나이틀리 적재 등 서버 배치 결과가 슬랙으로 갑니다. 웹훅 URL 은 배포
            시크릿(SLACK_WEBHOOK_URL)으로 주입되며, 시크릿이 비어 있으면 알림을 보내지
            않습니다. 크론은 매일 04:30(KST)에 돌고 신규·실패가 있었던 날만 알립니다.
          </p>

          <h2 style={{ marginTop: 28 }}>일일 사용 한도</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            계정별 하루 사용 횟수 한도입니다(한국시간 자정 초기화). 0 = 무제한, 빈값은 자리표시자에
            적힌 값이 적용됩니다. 저장 후 최대 1분 내 반영되며, 초과 시 사용자에게 안내 문구와 함께
            요청이 거절됩니다(429).
          </p>
          <div className="adm-filters" style={{ alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            {DAILY_FIELDS.map(([k, label, ph]) => (
              <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{label}</span>
                <input
                  className="adm-input"
                  style={{ width: 72 }}
                  inputMode="numeric"
                  placeholder={ph}
                  value={limits[k] ?? ""}
                  disabled={!user?.is_manager || settingBusy}
                  onChange={(e) => setLimits((p) => ({ ...p, [k]: e.target.value.replace(/[^0-9]/g, "") }))}
                />
              </label>
            ))}
          </div>

          <h2 style={{ marginTop: 24 }}>분당 사용 한도</h2>
          <p className="adm-empty" style={{ marginBottom: 12 }}>
            일 한도만으로는 &lsquo;1분에 300번&rsquo;을 막지 못합니다. 사람은 초당 클릭을 못 하지만
            수집 스크립트는 합니다. 원 데이터를 내리는 시장 이슈에만 겁니다.
          </p>
          <div className="adm-filters" style={{ alignItems: "center", flexWrap: "wrap", gap: 10 }}>
            {BURST_FIELDS.map(([k, label, ph]) => (
              <label key={k} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span>{label}</span>
                <input
                  className="adm-input"
                  style={{ width: 72 }}
                  inputMode="numeric"
                  placeholder={ph}
                  value={burst[k] ?? ""}
                  disabled={!user?.is_manager || settingBusy}
                  onChange={(e) => setBurst((p) => ({ ...p, [k]: e.target.value.replace(/[^0-9]/g, "") }))}
                />
              </label>
            ))}
            {user?.is_manager ? (
              <button
                className="rp-excel"
                type="button"
                disabled={settingBusy}
                onClick={() =>
                  void (async () => {
                    setSettingBusy(true);
                    try {
                      for (const [k] of DAILY_FIELDS) {
                        await updateSetting(`limit_daily_${k}`, (limits[k] ?? "").trim());
                      }
                      for (const [k] of BURST_FIELDS) {
                        await updateSetting(`limit_burst_${k}`, (burst[k] ?? "").trim());
                      }
                      setSettings(await fetchSettings());
                    } catch (er) {
                      setErr(String((er as Error).message || er));
                    } finally {
                      setSettingBusy(false);
                    }
                  })()
                }
              >
                한도 저장 (일·분 전체)
              </button>
            ) : null}
          </div>

          {user?.is_manager ? (
            <>
              <h2 style={{ marginTop: 28 }}>서비스 인증 게이트</h2>
              <p className="adm-empty" style={{ marginBottom: 12 }}>
                밸류에이션 API(valuation-2)의 무인증 직접 호출을 차단합니다. 정상 경로(이 에이전트·
                엑셀 애드인)는 영향 없습니다. 문제가 생기면 즉시 끄세요 — 배포 없이 1분 내 반영됩니다.
              </p>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={settings.svc_auth_required?.value === "1"}
                  disabled={settingBusy}
                  onChange={(e) =>
                    void (async () => {
                      setSettingBusy(true);
                      try {
                        await updateSetting("svc_auth_required", e.target.checked ? "1" : "0");
                        setSettings(await fetchSettings());
                      } catch (er) {
                        setErr(String((er as Error).message || er));
                      } finally {
                        setSettingBusy(false);
                      }
                    })()
                  }
                />
                <span>무인증 직접 호출 차단 {settings.svc_auth_required?.value === "1" ? "(켜짐)" : "(꺼짐)"}</span>
              </label>
            </>
          ) : null}

          <h2 style={{ marginTop: 28 }}>일일 사용량 (계정 × 기능)</h2>
          <div className="adm-filters" style={{ alignItems: "center", marginBottom: 8 }}>
            <select className="adm-input" value={usageDay} onChange={(e) => setUsageDay(e.target.value)}>
              {[...new Set(usage.map((r) => r.day))].sort().reverse().map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {(() => {
            const dayRows = usage.filter((r) => r.day === usageDay);
            if (!dayRows.length) return <p className="adm-empty">해당 날짜의 사용 기록이 없습니다.</p>;
            const byUser = new Map<string, Record<string, number>>();
            for (const r of dayRows) {
              const rec = byUser.get(r.user_uid) || {};
              // 유사기업은 세 기능(AI 추천·산업분류·공시선례) 합산
              const col = ["peer_ai", "industry", "precedent"].includes(r.feature) ? "peer" : r.feature;
              rec[col] = (rec[col] || 0) + r.uses;
              byUser.set(r.user_uid, rec);
            }
            const cols = [
              ["chat", "채팅"],
              ["buffett", "버핏"],
              ["peer", "유사기업"],
              ["dcf", "DCF"],
              ["report", "리포트"],
              ["re", "부동산"],
            ] as const;
            const rows = [...byUser.entries()].sort(
              (a, b) => Object.values(b[1]).reduce((x, y) => x + y, 0) - Object.values(a[1]).reduce((x, y) => x + y, 0),
            );
            return (
              <table className="adm-table">
                <thead>
                  <tr>
                    <th>계정</th>
                    {cols.map(([k, label]) => <th key={k} style={{ textAlign: "right" }}>{label}</th>)}
                    <th style={{ textAlign: "right" }}>합계</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(([uid, rec]) => (
                    <tr key={uid}>
                      <td className="mono">{uid}</td>
                      {cols.map(([k]) => (
                        <td key={k} style={{ textAlign: "right" }}>{rec[k] || ""}</td>
                      ))}
                      <td style={{ textAlign: "right", fontWeight: 600 }}>
                        {Object.values(rec).reduce((x, y) => x + y, 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </section>
      ) : null}

      {detail ? (
        <div className="adm-modal" role="dialog" aria-modal="true" aria-label="대화 전체">
          <div className="adm-modal-box">
            <header className="adm-modal-head">
              <h2>대화 전체</h2>
              <span className="mono adm-when">{detail.id}</span>
              <button className="rp-excel" type="button" onClick={() => setDetail(null)}>
                닫기
              </button>
            </header>
            <div className="adm-modal-body">
              {detail.items.map((l) => (
                <div className="adm-turn" key={l.id}>
                  <div className="adm-log-head">
                    <span className="adm-when">{when(l.created_at)}</span>
                    <span className="adm-badge">{ms(l.duration_ms)}</span>
                  </div>
                  <p className="adm-q">{l.question}</p>
                  <p className="adm-a full">{l.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      <ContactFooter />
    </div>
  );
}
